import { Injectable } from '@angular/core';
import { Device } from 'app/modules/admin/models/devices.types';
import { Routine } from 'app/modules/admin/models/routines.types';

/**
 * Servicio para manejar la lógica de asignación de dispositivos
 */
@Injectable()
export class DeviceAssignmentService {

    /**
     * Obtiene los dispositivos disponibles para un tipo específico
     * Filtra dispositivos ya usados y por división
     */
    getAvailableDevices(
        typeDeviceId: number,
        allDevices: Device[],
        selectedRoutines: Routine[],
        currentDeviceId?: number,
        routineDivisionIds?: number[]
    ): Device[] {
        // Crear set de IDs de dispositivos ya usados
        const usedDeviceIds = this.getUsedDeviceIds(selectedRoutines, currentDeviceId);

        // Filtrar dispositivos disponibles
        return allDevices.filter(device => {
            const isCorrectType = device.type_device_id === typeDeviceId;
            const isNotUsed = !usedDeviceIds.has(device.id) || device.id === currentDeviceId;
            const matchesDivision = this.matchesDivision(device, routineDivisionIds);

            return isCorrectType && isNotUsed && matchesDivision;
        });
    }

    /**
     * Ordena los dispositivos poniendo el seleccionado primero
     */
    sortDevicesWithSelectedFirst(devices: Device[], selectedDeviceId: number | null): Device[] {
        if (!selectedDeviceId) return devices;

        return [...devices].sort((a, b) => {
            if (a.id === selectedDeviceId) return -1;
            if (b.id === selectedDeviceId) return 1;
            return 0;
        });
    }

    /**
     * Encuentra el tipo de dispositivo por posición en una rutina
     */
    findTypeDeviceByPosition(routine: Routine, position: string) {
        return routine.cluster?.type_devices?.find(td => td.pivot?.position === position);
    }

    /**
     * Obtiene los IDs de dispositivos ya usados en las rutinas seleccionadas
     */
    private getUsedDeviceIds(selectedRoutines: Routine[], currentDeviceId?: number): Set<number> {
        const usedDeviceIds = new Set<number>();

        for (const routine of selectedRoutines) {
            if (!routine?.cluster?.type_devices) continue;

            for (const td of routine.cluster.type_devices) {
                if (td.selected_device_id && td.selected_device_id !== currentDeviceId) {
                    usedDeviceIds.add(td.selected_device_id);
                }
            }
        }

        return usedDeviceIds;
    }

    /**
     * Verifica si un dispositivo coincide con las divisiones especificadas
     */
    private matchesDivision(device: Device, divisionIds?: number[]): boolean {
        if (!divisionIds || divisionIds.length === 0) return true;
        return device.divisions?.some(d => divisionIds.includes(d.id)) ?? false;
    }

    /**
     * Crea un mapa de posiciones a alias de dispositivos para una rutina
     */
    createDeviceAliasMap(routine: Routine, allDevices: Device[]): { [key: string]: string } {
        const map: { [key: string]: string } = {};

        if (!routine?.cluster?.type_devices) return map;

        for (const td of routine.cluster.type_devices) {
            if (td.selected_device_id && td.pivot?.position) {
                const device = allDevices.find(d => d.id === td.selected_device_id);
                map[td.pivot.position] = this.getDeviceDisplayName(device);
            }
        }

        return map;
    }

    /**
     * Obtiene el nombre a mostrar de un dispositivo (alias o serial)
     */
    private getDeviceDisplayName(device: Device | undefined): string {
        if (!device) return 'Sin alias';

        if (Array.isArray(device.alias)) {
            return device.alias[0]?.alias ?? device.serial ?? 'Sin alias';
        }

        return device.alias || device.serial || 'Sin alias';
    }

    /**
     * Verifica si un dispositivo es válido basado en su licencia
     */
    isDeviceValid(device: any): boolean {
        if (!device.license) {
            return false;
        }
        if (device.license.expiration_date === null) {
            return true;
        }
        const today = new Date().toISOString().split('T')[0];
        return device.license.expiration_date >= today;
    }
}
